/**
 * LiveQueueColumn · v2.7.65+E150
 * ═════════════════════════════════════════════════════════════════════
 * Real-time dispatch queue column. Drop this into the Ride Coordinator
 * or any admin panel to see every queued_trip_assigned frame land as it
 * happens. Supports Reassign (change driver) + Cancel actions.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Loader2, Radio, MapPin, User, X, Users, CalendarClock, Clock, DollarSign, ShieldCheck, CalendarDays, Pencil, Check, CheckCheck, AlertTriangle, Smartphone, Calculator, Flag, Search, Trash2, Phone, Navigation, Copy, PhoneCall, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtUsd } from "@/lib/format";
import RiderFlagsBadge from "@/components/RiderFlagsBadge";
import { hasCriticalFlag, RIDER_FLAGS, isCriticalFlag } from "@/lib/riderFlags";

// v2.8+E322 · Shared rider-flag add/remove editor. Tapping a flag toggles it
// and persists immediately via onToggle → PATCH /admin/queued-trips/{id}.
// Used both inline on the card face and inside the detail modal.
function FlagEditor({ flags, onToggle, busy, idPrefix }) {
  const active = Array.isArray(flags) ? flags : [];
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid={`${idPrefix}-flag-editor`}>
      {RIDER_FLAGS.map((f) => {
        const on = active.includes(f);
        const crit = isCriticalFlag(f);
        return (
          <button
            type="button"
            key={f}
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onToggle(f); }}
            aria-pressed={on}
            data-testid={`${idPrefix}-flag-${f.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-semibold text-left transition-colors ${on ? (crit ? "border-red-400 bg-red-600/30 text-white font-bold" : "border-red-500 bg-red-500/20 text-red-200") : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-zinc-500"} ${busy ? "opacity-50 cursor-wait" : ""}`}
          >
            <span className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${on ? "bg-red-500 border-red-500" : "border-zinc-600"}`}>
              {on && <Check className="h-2.5 w-2.5 text-white"/>}
            </span>
            {f}
          </button>
        );
      })}
    </div>
  );
}

// v2.8+E292 · Compact label/value row for the card detail modal.
function DetailRow({ icon: Icon, label, value, testid, valueClass = "" }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0"/>}
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
        <div className={`text-zinc-100 break-words ${valueClass}`} data-testid={testid}>{value}</div>
      </div>
    </div>
  );
}

// v2.7.65+E177 · Ride Coordinator booking-details renderer. Every field
// falls back to "Not recorded" so legacy rows written before this ledger
// existed still display cleanly in the queue card.
const NOT_RECORDED = "Not recorded";

function fmtDateTime(iso) {
  if (!iso) return NOT_RECORDED;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return NOT_RECORDED;
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return NOT_RECORDED; }
}

// v2.7.65+E195 · Time-only helper for the "Requested Transport" field
// (the rider's requested pickup clock time — no calendar date). Used
// alongside the separate Pre-Book badge so a future-day booking shows
// both the date (badge) and the pickup time (Requested Transport field).
function fmtTime(iso) {
  if (!iso) return NOT_RECORDED;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return NOT_RECORDED;
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return NOT_RECORDED; }
}

// v2.7.65+E195 · Long-form date-only helper for the Pre-Book badge
// (e.g. "Mon, Feb 12"). Renders without the year unless the target
// date is in a different calendar year than today.
function fmtPreBookDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const opts = { weekday: "short", month: "short", day: "numeric" };
    if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
    return d.toLocaleDateString(undefined, opts);
  } catch { return ""; }
}

// v2.7.65+E195 · Returns true when a scheduled_pickup_at ISO string
// falls on a calendar date AFTER today. Same-day scheduled pickups
// (e.g. a ride booked at 10:00 for a 14:00 pickup) are treated as
// same-day scheduled, NOT pre-book. Only truly future-date rides
// surface the Pre-Book badge.
function isPreBook(iso) {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    const startOfPickupDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return startOfPickupDay.getTime() > startOfToday.getTime();
  } catch { return false; }
}

function fmtQuoted(v) {
  if (v === null || v === undefined || v === "" || Number(v) === 0) return NOT_RECORDED;
  const n = Number(v);
  if (Number.isNaN(n)) return NOT_RECORDED;
  return `$${n.toFixed(2)}`;
}

// PROMPT 3 · Dispatcher dual notes — a PRIVATE internal note (never sent to the
// driver) and a DRIVER-VISIBLE note, each auto-saving (debounced 800ms) as you
// type. Works on the card face and inside the detail modal.
function NoteSaveStatus({ state }) {
  if (state === "saving") return <span className="text-[9px] text-zinc-500 flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin"/>Saving…</span>;
  if (state === "saved")  return <span className="text-[9px] text-emerald-400 flex items-center gap-1"><Check className="h-2.5 w-2.5"/>Saved</span>;
  return null;
}

function DualNotes({ row, onAutoSave, idPrefix }) {
  const [internalVal, setInternalVal] = useState(row.internal_notes ?? "");
  const [driverVal,   setDriverVal]   = useState(row.notes ?? row.dispatch_notes ?? "");
  const [internalStatus, setInternalStatus] = useState("idle");
  const [driverStatus,   setDriverStatus]   = useState("idle");
  const internalSaved = useRef(row.internal_notes ?? "");
  const driverSaved   = useRef(row.notes ?? row.dispatch_notes ?? "");

  // Adopt async server updates ONLY when the field isn't dirty (local ===
  // last-saved) so a 20s poll can't clobber what the dispatcher is typing.
  useEffect(() => {
    const server = row.internal_notes ?? "";
    if (internalVal === internalSaved.current && internalVal !== server) {
      setInternalVal(server); internalSaved.current = server;
    }
  }, [row.internal_notes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const server = row.notes ?? row.dispatch_notes ?? "";
    if (driverVal === driverSaved.current && driverVal !== server) {
      setDriverVal(server); driverSaved.current = server;
    }
  }, [row.notes, row.dispatch_notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-save per field.
  useEffect(() => {
    if (internalVal === internalSaved.current) return;
    setInternalStatus("saving");
    const t = setTimeout(async () => {
      const ok = await onAutoSave(row.id, { internal_notes: internalVal });
      if (ok) { internalSaved.current = internalVal; setInternalStatus("saved"); setTimeout(() => setInternalStatus("idle"), 1500); }
      else setInternalStatus("idle");
    }, 800);
    return () => clearTimeout(t);
  }, [internalVal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (driverVal === driverSaved.current) return;
    setDriverStatus("saving");
    const t = setTimeout(async () => {
      const ok = await onAutoSave(row.id, { notes: driverVal });
      if (ok) { driverSaved.current = driverVal; setDriverStatus("saved"); setTimeout(() => setDriverStatus("idle"), 1500); }
      else setDriverStatus("idle");
    }, 800);
    return () => clearTimeout(t);
  }, [driverVal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950/50 p-2"
      data-testid={`${idPrefix}-notes-block`}
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[9px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
            <Lock className="h-2.5 w-2.5"/>Internal Dispatch Notes · private
          </div>
          <NoteSaveStatus state={internalStatus}/>
        </div>
        <textarea
          value={internalVal} onChange={(e) => setInternalVal(e.target.value)}
          rows={2} placeholder="Private note — never shown to the driver…"
          data-testid={`${idPrefix}-internal-notes-input`}
          className="w-full rounded-md border border-amber-500/30 bg-zinc-950 text-xs text-zinc-100 p-2 resize-none focus:border-amber-400/60 outline-none"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[9px] uppercase tracking-widest text-sky-300 font-bold flex items-center gap-1">
            <User className="h-2.5 w-2.5"/>Driver Notes · visible to driver
          </div>
          <NoteSaveStatus state={driverStatus}/>
        </div>
        <textarea
          value={driverVal} onChange={(e) => setDriverVal(e.target.value)}
          rows={2} placeholder="Note the assigned driver will see…"
          data-testid={`${idPrefix}-driver-notes-input`}
          className="w-full rounded-md border border-sky-500/30 bg-zinc-950 text-xs text-zinc-100 p-2 resize-none focus:border-sky-400/60 outline-none"
        />
      </div>
    </div>
  );
}

// v2.8+E290 · Auto-escalate — any UNASSIGNED ride waiting longer than this
// flips to URGENT visually (red badge + top-of-queue sort) with no DB write,
// so it shows live + consistently for every dispatcher on the board. A
// manual flag (row.urgent) still wins and persists.
const AUTO_ESCALATE_MS = 5 * 60 * 1000; // 5 minutes
// v2.8+E318 · An assigned ride whose dispatch note is still unopened after
// this long gets flagged so dispatch can call the driver.
const NOTE_UNREAD_NUDGE_MS = 3 * 60 * 1000; // 3 minutes
const waitMs = (r) => (r.created_at ? Date.now() - new Date(r.created_at).getTime() : 0);
// v2.8+E318 · Compact "time ago" for the notes read-receipt badge. Recomputed
// on the board's 30s tick so it stays roughly live without extra timers.
const fmtAgo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};
// v2.8+E318 · Compact elapsed duration (no "ago") for the unread-note nudge.
const fmtDurMin = (min) =>
  min < 60 ? `${min}m`
    : min < 1440 ? `${Math.floor(min / 60)}h ${min % 60}m`
    : `${Math.floor(min / 1440)}d`;
const isStaleUnassigned = (r) =>
  !r.assigned_driver_id && r.state === "queued" && !!r.created_at && waitMs(r) >= AUTO_ESCALATE_MS;
const isEffUrgent = (r) => !!r.urgent || isStaleUnassigned(r);


function QueueCard({ row, drivers, onCancel, onReassign, onComplete, onPatch, onQuote, onAssignNearest, onAutoSave, busyId }) {
  const [showReassign, setShowReassign] = useState(false);
  const [pick, setPick] = useState("");
  const [reason, setReason] = useState("");
  const busy = busyId === row.id;

  // v2.8+E290 · Effective urgency = manual flag OR auto-escalated (stale
  // unassigned). autoOnly drives the "· auto" hint + wait minutes.
  const autoOnly = isStaleUnassigned(row) && !row.urgent;
  const urgent = !!row.urgent || autoOnly;
  const waitMin = Math.floor(waitMs(row) / 60000);
  const waitLabel = waitMin < 1 ? "<1m"
    : waitMin < 60 ? `${waitMin}m`
    : waitMin < 1440 ? `${Math.floor(waitMin / 60)}h ${waitMin % 60}m`
    : `${Math.floor(waitMin / 1440)}d`;
  const isUnassigned = !row.assigned_driver_id && row.state === "queued";
  const isCompleted = row.state === "completed";
  // v2.8+E318 · Dispatch-note nudge — an assigned ride whose note is still
  // unopened after NOTE_UNREAD_NUDGE_MS gets flagged so dispatch can call.
  const noteAssignedAt = row.assigned_at || row.created_at;
  const noteUnreadMin = noteAssignedAt ? Math.floor((Date.now() - new Date(noteAssignedAt).getTime()) / 60000) : 0;
  const noteNudge = !!row.dispatch_notes && !row.dispatch_notes_viewed_at && !!row.assigned_driver_id && noteUnreadMin >= (NOTE_UNREAD_NUDGE_MS / 60000);

  // v2.8+E281 · Rider-initiated (online) bookings get dispatch-side edit
  // controls: editable fare, driver-visible notes, and a Complete button.
  // admin_dispatch cards keep their existing controls untouched.
  const isOnline = row.source === "rider_account" || row.source === "public_quick_booking";
  const [editPrice, setEditPrice] = useState(false);
  const [priceVal, setPriceVal] = useState(row.total != null ? String(row.total) : "");
  // v2.8+E284 · Keep local price in sync with async server updates so a stale
  // Save can't revert a fare the Rider App stamped after this card rendered.
  useEffect(() => {
    if (!editPrice) setPriceVal(row.total != null ? String(row.total) : "");
  }, [row.total, editPrice]);
  // v2.8+E282 · One-tap "Quote from addresses" — fills the price from the
  // fare engine; dispatch can still hand-type it.
  const [quoting, setQuoting] = useState(false);
  const [quoteHint, setQuoteHint] = useState("");
  // v2.8+E292 · Clickable detail modal.
  const [showDetail, setShowDetail] = useState(false);
  // v2.8+E322 · Flag-from-coordinator — inline add/remove flags on the card.
  const [showFlags, setShowFlags] = useState(false);
  const toggleFlag = (f) => {
    const active = row.rider_flags || [];
    const next = active.includes(f) ? active.filter((x) => x !== f) : [...active, f];
    onPatch(row.id, { rider_flags: next });
  };

  const submitReassign = async () => {
    if (!pick) return;
    const picked = drivers.find((d) => d.id === pick);
    await onReassign(row.id, pick, picked?.name || "", !!row.assigned_driver_id, reason.trim() || undefined);
    setShowReassign(false);
    setPick("");
    setReason("");
  };

  const savePrice = async () => {
    const n = parseFloat(priceVal);
    if (priceVal === "" || Number.isNaN(n) || n < 0) { toast.error("Enter a valid price"); return; }
    const ok = await onPatch(row.id, { total: n });
    if (ok) setEditPrice(false);
  };

  const doQuote = async () => {
    setQuoting(true);
    try {
      const data = await onQuote(row.id);
      if (data) {
        setPriceVal(String(data.total));
        setEditPrice(true);
        setQuoteHint(`Quoted $${Number(data.total).toFixed(2)} · ${data.miles} mi${data.round_trip ? " · round trip" : ""}`);
      }
    } finally { setQuoting(false); }
  };

  // v2.8+E292 · Unsaved-edit guard for the detail modal + source label.
  const priceDirty = editPrice && priceVal !== (row.total != null ? String(row.total) : "");
  const hasUnsaved = priceDirty;
  const requestCloseDetail = () => {
    if (hasUnsaved && !window.confirm("You have an unsaved price edit. Close the detail view?\n\nYour edit stays on the card but won't be applied until you Save.")) return;
    setShowDetail(false);
  };
  const sourceLabel = row.source === "rider_account" ? "Rider Portal"
    : row.source === "admin_dispatch" ? "Dispatch"
    : row.source === "public_quick_booking" ? "Public · Web"
    : (row.source || NOT_RECORDED);

  return (
    <>
    <Card
      onClick={(e) => {
        // Open the detail modal on a card tap, but let inline controls work.
        if (e.target.closest('button, input, textarea, select, a, [role="combobox"], ol')) return;
        setShowDetail(true);
      }}
      title="Click for full booking details"
      className={`p-3 space-y-2 border cursor-pointer transition hover:ring-1 hover:ring-inset hover:ring-white/10 ${row.rider_flags?.length ? (hasCriticalFlag(row.rider_flags) ? "ring-2 ring-red-500 " : "ring-2 ring-red-500/60 ") : ""}${
        isCompleted                 ? "border-zinc-700 bg-zinc-800/20" :
        urgent                      ? "border-red-500/70 bg-red-500/[0.06]" :
        row.state === "in_progress" ? "border-emerald-400/50" :
        !row.assigned_driver_id     ? "border-amber-400/60 bg-amber-500/[0.03]" :
                                       "border-zinc-800"
      }`}
      data-testid={`queue-card-${row.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-bold">
            {isCompleted ? (
              <span className="text-emerald-300/90 inline-flex items-center gap-0.5"><CheckCheck className="h-2.5 w-2.5"/>COMPLETED</span>
            ) : !row.assigned_driver_id ? (
              <span className="text-amber-400">UNASSIGNED</span>
            ) : row.state === "in_progress" ? (
              <span className="text-emerald-400">IN PROGRESS</span>
            ) : (
              <span className="text-sky-400">ASSIGNED</span>
            )}
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-500 font-mono">{row.created_at ? new Date(row.created_at).toLocaleTimeString() : "—"}</span>
            {urgent && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/60 text-[9px] font-black animate-pulse" data-testid={`queue-card-${row.id}-urgent-badge`}>
                <Flag className="h-2.5 w-2.5"/>URGENT{autoOnly ? ` · auto ${waitLabel}` : ""}
              </span>
            )}
            {isUnassigned && (
              <span
                className={`inline-flex items-center gap-0.5 font-mono normal-case ${
                  waitMin >= 5 ? "text-red-300" : waitMin >= 3 ? "text-amber-300" : "text-zinc-500"
                }`}
                data-testid={`queue-card-${row.id}-wait`}
                title="How long this ride has waited unassigned"
              >
                <Clock className="h-2.5 w-2.5"/>waiting {waitLabel}
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-zinc-100 mt-0.5 truncate">{row.rider_name || "Rider"}</div>
          {/* v2.8+E319 · Prominent rider-flag chip (full list in detail modal). */}
          {row.rider_flags?.length > 0 && (
            <div className="mt-1">
              <RiderFlagsBadge flags={row.rider_flags} mode="chip" testId={`queue-card-${row.id}-flags`}/>
            </div>
          )}
          <div className="text-[11px] text-zinc-500 flex items-center gap-1 min-w-0" title={`${row.pickup_address || "—"} → ${row.dropoff_label || "—"}`}>
            <MapPin className="h-3 w-3 shrink-0"/><span className="truncate"><span className="text-yellow-400">{row.pickup_address || "—"}</span> → <span className="text-yellow-400">{row.dropoff_label}</span></span>
          </div>
          {row.assigned_driver_name && (
            <div className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5">
              <User className="h-3 w-3"/><span className="truncate">{row.assigned_driver_name}</span>
            </div>
          )}
          {/* v2.8+E317 · Dispatch-notes read receipt — always visible on the
              card so dispatch instantly knows if the driver opened the note. */}
          {row.dispatch_notes && (
            row.dispatch_notes_viewed_at ? (
              <div
                className="text-[10px] text-emerald-400 font-bold flex items-center gap-1 mt-0.5"
                data-testid={`queue-card-${row.id}-notes-viewed`}
                title={`Driver viewed notes · ${new Date(row.dispatch_notes_viewed_at).toLocaleString()}`}
              >
                <CheckCheck className="h-3 w-3"/>Notes viewed {fmtAgo(row.dispatch_notes_viewed_at)}
              </div>
            ) : row.assigned_driver_id ? (
              noteNudge ? (
                <div
                  className="text-[10px] text-red-300 font-bold flex items-center gap-1 mt-0.5 animate-pulse"
                  data-testid={`queue-card-${row.id}-notes-nudge`}
                  title="Driver hasn't opened the dispatch note yet — consider calling them"
                >
                  <AlertTriangle className="h-3 w-3"/>Note unread {fmtDurMin(noteUnreadMin)} · call driver
                </div>
              ) : (
                <div
                  className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5"
                  data-testid={`queue-card-${row.id}-notes-unviewed`}
                  title="Note sent to driver — not opened yet"
                >
                  <Check className="h-3 w-3"/>Notes sent · awaiting driver view
                </div>
              )
            ) : null
          )}
          {/* v2.7.65+E159 · Dispatcher attribution — who typed the booking. */}
          {row.created_by_admin_name && (
            <div
              className="text-[10px] text-yellow-300/90 flex items-center gap-1 mt-0.5"
              data-testid={`queue-card-${row.id}-attribution`}
              title="Dispatch agent who submitted this booking"
            >
              <User className="h-3 w-3"/>
              <span className="truncate">by {row.created_by_admin_name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-[10px] text-zinc-500 mt-1">
            <span className="flex items-center gap-1"><Users className="h-3 w-3"/>{row.passengers}</span>
            {/* v2.7.65+E177 · Explicit CCVAP badge — more prominent than
                just the voucher serial. Rendered when either the new
                `is_ccvap` flag is true OR a legacy `voucher_code` is
                present so old rows still light up. */}
            {(row.is_ccvap || row.voucher_code) && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-black bg-amber-500/20 text-amber-200 border border-amber-400/60"
                data-testid={`queue-card-${row.id}-ccvap-badge`}
                title="Community Care Voucher Assistance Program recipient"
              >
                <ShieldCheck className="h-2.5 w-2.5"/>CCVAP
                {row.voucher_code && (
                  <span className="font-mono opacity-90">· {row.voucher_code}</span>
                )}
              </span>
            )}
            {/* v2.7.65+E158 · Source badges — cyan for dispatcher-created,
                fuchsia for public web bookings so operators can tell
                dispatch-agent submissions apart from customer bookings
                at a glance. */}
            {row.source === "admin_dispatch" && (
              <span
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-black bg-yellow-500/25 text-yellow-200 border border-yellow-400/60"
                data-testid={`queue-card-${row.id}-source-dispatch`}
                title="Created by a dispatch agent"
              >
                Dispatch Booking
              </span>
            )}
            {row.source === "public_quick_booking" && (
              <span
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40"
                data-testid={`queue-card-${row.id}-source-public`}
                title="Submitted from the public homepage Quick Booking form"
              >
                Public · Web
              </span>
            )}
            {row.source === "rider_account" && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold bg-violet-500/20 text-violet-200 border border-violet-400/40"
                data-testid={`queue-card-${row.id}-source-rider`}
                title="Booked by a signed-in rider from the Rider Account Portal"
              >
                <Smartphone className="h-2.5 w-2.5"/>Rider App
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-black text-amber-400 tabular-nums">{fmtUsd(row.total)}</div>
        </div>
      </div>

      {/* v2.7.65+E195 · Booking-details block (Ride Coordinator).
          Explicitly labels the three time fields the operator asked
          for, plus keeps the quoted-fare-at-booking readout so the
          intake price stays visible even after the driver adds fees.
              1. Booking Created Time — when the rider hit "Book Now"
              2. Requested Transport Time — the pickup clock time
              3. Pre-Book Date · badge · only shown when the ride is
                 scheduled for a FUTURE calendar day (not just later
                 today). Prominent amber pill so operators can spot
                 tomorrow-and-beyond bookings at a glance.
          Purely display — pulls existing fields from the booking
          record (no logic, form, or backend changes). */}
      {isPreBook(row.scheduled_pickup_at) && (
        <div
          className="flex items-center gap-1.5 rounded-md border border-amber-400/60 bg-amber-500/[0.12] px-2 py-1"
          data-testid={`queue-card-${row.id}-prebook-badge`}
          title="This ride was scheduled for a future date"
        >
          <CalendarDays className="h-3.5 w-3.5 text-amber-300"/>
          <span className="text-[10px] uppercase tracking-widest font-black text-amber-300">Pre-Book</span>
          <span className="text-[11px] font-bold text-amber-100 ml-1">
            {fmtPreBookDate(row.scheduled_pickup_at)}
          </span>
        </div>
      )}
      <div
        className="rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 grid grid-cols-3 gap-2 text-[10px]"
        data-testid={`queue-card-${row.id}-booking-details`}
      >
        <div className="min-w-0">
          <div className="uppercase tracking-widest text-[8.5px] font-bold text-zinc-500 flex items-center gap-1">
            <Clock className="h-2.5 w-2.5"/>Booking Created
          </div>
          <div
            className="text-zinc-200 font-mono truncate mt-0.5"
            data-testid={`queue-card-${row.id}-booked-at`}
            title={row.booking_created_at || row.created_at || NOT_RECORDED}
          >
            {fmtDateTime(row.booking_created_at || row.created_at)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="uppercase tracking-widest text-[8.5px] font-bold text-zinc-500 flex items-center gap-1">
            <CalendarClock className="h-2.5 w-2.5"/>Requested Transport
          </div>
          <div
            className="text-zinc-200 font-mono truncate mt-0.5"
            data-testid={`queue-card-${row.id}-scheduled-pickup`}
            title={row.scheduled_pickup_at || "ASAP or Not recorded"}
          >
            {row.scheduled_pickup_at
              ? fmtTime(row.scheduled_pickup_at)
              : (row.scheduled_pickup_at === null && row.source === "admin_dispatch"
                  ? <span className="text-emerald-300">ASAP</span>
                  : NOT_RECORDED)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="uppercase tracking-widest text-[8.5px] font-bold text-zinc-500 flex items-center gap-1">
            <DollarSign className="h-2.5 w-2.5"/>Quoted
          </div>
          <div
            className="text-amber-300 font-mono truncate mt-0.5"
            data-testid={`queue-card-${row.id}-quoted-fare`}
            title="Quoted fare captured at booking time"
          >
            {fmtQuoted(row.quoted_fare_at_booking)}
          </div>
        </div>
      </div>

      {/* PROMPT 3 · Dual notes — private internal + driver-visible, auto-save. */}
      <DualNotes row={row} onAutoSave={onAutoSave} idPrefix={`queue-card-${row.id}`}/>

      {!isCompleted && isOnline && (
        <div className="space-y-2 rounded-md border border-fuchsia-500/25 bg-fuchsia-500/[0.04] p-2" data-testid={`queue-card-${row.id}-online-edit`}>
          <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-bold">Dispatch edit · online booking</div>
          {/* Editable fare (dispatch re-quotes rider bookings before assigning) */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold w-12 shrink-0">Price</span>
            {editPrice ? (
              <>
                <div className="relative flex-1 min-w-0">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                  <input
                    type="number" min="0" step="0.01" value={priceVal}
                    onChange={(e) => setPriceVal(e.target.value)}
                    className="w-full h-8 pl-5 pr-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100"
                    data-testid={`queue-card-${row.id}-price-input`}
                  />
                </div>
                <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold" disabled={busy}
                  data-testid={`queue-card-${row.id}-price-save`} onClick={savePrice}>
                  {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : "Save"}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-zinc-400"
                  onClick={() => { setEditPrice(false); setPriceVal(row.total != null ? String(row.total) : ""); }}>Cancel</Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-black text-amber-400 tabular-nums" data-testid={`queue-card-${row.id}-price-display`}>{fmtUsd(row.total)}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditPrice(true)} data-testid={`queue-card-${row.id}-price-edit`}>
                  <Pencil className="h-3 w-3 mr-1"/>Edit price
                </Button>
              </>
            )}
          </div>
          {/* One-tap quote from the pickup + destination addresses */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs border-sky-500/40 text-sky-300 hover:text-sky-200"
              disabled={quoting} onClick={doQuote} data-testid={`queue-card-${row.id}-quote`}>
              {quoting ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <Calculator className="h-3 w-3 mr-1"/>}
              Quote from addresses
            </Button>
            {quoteHint && (
              <span className="text-[10px] text-sky-300/90 truncate" data-testid={`queue-card-${row.id}-quote-hint`}>{quoteHint} — review &amp; Save</span>
            )}
          </div>
        </div>
      )}

      {!isCompleted && (showReassign ? (
        <div className="space-y-1">
          <div className="flex gap-1">
            <select
              value={pick} onChange={(e) => setPick(e.target.value)}
              className="flex-1 h-8 px-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100"
              data-testid={`queue-card-${row.id}-reassign-select`}
            >
              <option value="">Pick a driver…</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.online ? "🟢" : "⚪"} {d.name}</option>
              ))}
            </select>
            <Button size="sm" onClick={submitReassign} disabled={!pick || busy} data-testid={`queue-card-${row.id}-reassign-submit`} className="h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold">
              {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : "OK"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowReassign(false); setReason(""); }} className="h-8 text-zinc-400">Cancel</Button>
          </div>
          <input
            type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for handoff (optional)" maxLength={300}
            data-testid={`queue-card-${row.id}-reassign-reason`}
            className="w-full h-7 px-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100 placeholder:text-zinc-600"
          />
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowReassign(true)} data-testid={`queue-card-${row.id}-reassign`} className="h-7 text-xs flex-1 min-w-[92px]">
            {row.assigned_driver_id ? "Reassign" : "Assign driver"}
          </Button>
          <Button
            size="sm" variant="outline" onClick={() => onAssignNearest(row.id)} disabled={busy}
            data-testid={`queue-card-${row.id}-nearest`}
            title="Hand off to the nearest online driver — no scrolling"
            className="h-7 px-2 text-xs border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Navigation className="h-3.5 w-3.5 mr-1"/>Nearest</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPatch(row.id, { urgent: !row.urgent })}
            disabled={busy}
            data-testid={`queue-card-${row.id}-urgent`}
            title={row.urgent ? "Remove urgent flag" : "Flag urgent — jumps to the top of the board"}
            className={`h-7 px-2 ${row.urgent ? "border-red-500/70 text-red-300 bg-red-500/10 hover:bg-red-500/20" : "border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/60"}`}
          >
            <Flag className={`h-3.5 w-3.5 ${row.urgent ? "fill-red-400/50" : ""}`}/>
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); setShowFlags((s) => !s); }}
            disabled={busy}
            data-testid={`queue-card-${row.id}-flags-toggle`}
            title="Add or remove rider safety flags"
            className={`h-7 px-2 text-xs ${row.rider_flags?.length ? "border-red-500/70 text-red-300 bg-red-500/10 hover:bg-red-500/20" : "border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/60"}`}
          >
            <Flag className="h-3.5 w-3.5 mr-1"/>Flags{row.rider_flags?.length ? ` · ${row.rider_flags.length}` : ""}
          </Button>
          {isOnline && (
            <Button size="sm" onClick={() => onComplete(row.id)} disabled={busy} data-testid={`queue-card-${row.id}-complete`} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
              {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Check className="h-3.5 w-3.5 mr-1"/>Complete</>}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onCancel(row.id)} data-testid={`queue-card-${row.id}-cancel`} className="h-7 text-rose-400 hover:text-rose-300">
            <X className="h-3.5 w-3.5"/>
          </Button>
        </div>
      ))}
      {/* v2.8+E322 · Inline flag editor — quick add/remove on the card face. */}
      {!isCompleted && showFlags && (
        <div
          className="pt-2 mt-1 border-t border-zinc-800/60 space-y-1"
          onClick={(e) => e.stopPropagation()}
          data-testid={`queue-card-${row.id}-flag-editor-wrap`}
        >
          <div className="text-[9px] uppercase tracking-widest text-red-300 font-bold flex items-center gap-1">
            <Flag className="h-3 w-3"/>Rider Flags — tap to add or remove
          </div>
          <FlagEditor flags={row.rider_flags} onToggle={toggleFlag} busy={busy} idPrefix={`queue-card-${row.id}`}/>
        </div>
      )}
    </Card>

    {/* v2.8+E292 · Card detail modal — full booking info + all actions.
        Reuses the same shared state/handlers as the inline card controls,
        so edits made here reflect on the card. Works desktop + mobile. */}
    <Dialog open={showDetail} onOpenChange={(o) => { if (!o) requestCloseDetail(); }}>
      <DialogContent
        className="max-w-lg w-[92vw] max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700"
        data-testid={`queue-detail-${row.id}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100 pr-6">
            <span className="truncate">{row.rider_name || "Rider"}</span>
            {urgent && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/60 text-[9px] font-black shrink-0">
                <Flag className="h-2.5 w-2.5"/>URGENT
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Customer */}
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Customer</div>
            <DetailRow icon={User}  label="Full name" value={row.rider_name || NOT_RECORDED} testid={`queue-detail-${row.id}-name`}/>
            <div className="flex items-start gap-2">
              <Phone className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Phone</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-100 font-mono break-all" data-testid={`queue-detail-${row.id}-phone`}>{row.rider_phone || NOT_RECORDED}</span>
                  {row.rider_phone && (
                    <>
                      <button
                        onClick={() => { try { navigator.clipboard?.writeText(row.rider_phone); toast.success("Phone number copied"); } catch { toast.error("Copy not available"); } }}
                        data-testid={`queue-detail-${row.id}-phone-copy`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-300 hover:text-sky-200 border border-sky-500/40 hover:border-sky-400 rounded px-1.5 py-0.5 transition-colors"
                      >
                        <Copy className="h-3 w-3"/>Copy
                      </button>
                      <a
                        href={`tel:${row.rider_phone}`}
                        data-testid={`queue-detail-${row.id}-phone-call`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 hover:border-emerald-400 rounded px-1.5 py-0.5 transition-colors"
                      >
                        <PhoneCall className="h-3 w-3"/>Call
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* v2.8+E319/E320/E322 · Rider flags — list + audit + add/remove editor. */}
          <section className="space-y-2 border-t border-zinc-800 pt-3" data-testid={`queue-detail-${row.id}-flags`}>
            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Rider Flags</div>
            {row.rider_flags?.length > 0 ? (
              <>
                <RiderFlagsBadge flags={row.rider_flags} mode="list" testId={`queue-detail-${row.id}-flags-badge`}/>
                {row.rider_flags_audit?.length > 0 && (
                  <div className="space-y-1 pt-0.5" data-testid={`queue-detail-${row.id}-flags-audit`}>
                    {row.rider_flags_audit.map((a, i) => (
                      <div key={i} className="text-[10px] text-zinc-400 flex items-start gap-1.5">
                        <Flag className="h-2.5 w-2.5 text-red-400 mt-0.5 flex-shrink-0"/>
                        <span><span className="font-bold text-zinc-200">{a.flag}</span>
                          <span className="text-zinc-500"> · added by {a.by || "Dispatch"}{a.at ? ` · ${new Date(a.at).toLocaleString()}` : ""}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* v2.8+E321 · Driver flag-acknowledgment receipt. */}
                {row.flags_acked_at && (
                  <div className="text-[10px] text-emerald-400 font-bold flex items-center gap-1.5" data-testid={`queue-detail-${row.id}-flags-acked`}>
                    <CheckCheck className="h-3 w-3"/>Acknowledged by {row.flags_acked_by || "driver"} · {new Date(row.flags_acked_at).toLocaleString()}
                  </div>
                )}
              </>
            ) : (
              <div className="text-[11px] text-zinc-500" data-testid={`queue-detail-${row.id}-flags-none`}>No flags on this ride.</div>
            )}
            {!isCompleted && (
              <>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold pt-1">Add / remove flags</div>
                <FlagEditor flags={row.rider_flags} onToggle={toggleFlag} busy={busy} idPrefix={`queue-detail-${row.id}`}/>
              </>
            )}
          </section>
          {/* Trip */}
          <section className="space-y-2 border-t border-zinc-800 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Trip</div>
            <DetailRow icon={MapPin} label="Pickup"   value={row.pickup_address || NOT_RECORDED} testid={`queue-detail-${row.id}-pickup`}/>
            <DetailRow icon={MapPin} label="Drop-off" value={row.dropoff_address || row.dropoff_label || NOT_RECORDED} testid={`queue-detail-${row.id}-dropoff`}/>
            <div className="flex items-start gap-2" data-testid={`queue-detail-${row.id}-stops`}>
              <Navigation className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Extra stops</div>
                {Array.isArray(row.stops) && row.stops.length > 0 ? (
                  <ol className="list-decimal ml-4 text-zinc-100 space-y-0.5">
                    {row.stops.map((s, i) => (
                      <li key={i} className="break-words">{typeof s === "string" ? s : (s.address || s.label || s.name || JSON.stringify(s))}</li>
                    ))}
                  </ol>
                ) : Number(row.extra_stops) > 0 ? (
                  <div className="text-zinc-100">{row.extra_stops} extra stop{Number(row.extra_stops) > 1 ? "s" : ""} <span className="text-zinc-500">(addresses not recorded)</span></div>
                ) : (
                  <div className="text-zinc-500">None</div>
                )}
              </div>
            </div>
            <DetailRow icon={CalendarClock} label="Requested pickup" value={row.scheduled_pickup_at ? fmtDateTime(row.scheduled_pickup_at) : "ASAP"} testid={`queue-detail-${row.id}-pickup-time`}/>
            {isPreBook(row.scheduled_pickup_at) && (
              <DetailRow icon={CalendarDays} label="Reservation (pre-booked)" value={fmtDateTime(row.scheduled_pickup_at)} testid={`queue-detail-${row.id}-reservation`} valueClass="text-amber-200 font-bold"/>
            )}
          </section>

          {/* Booking */}
          <section className="space-y-2 border-t border-zinc-800 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Booking</div>
            <DetailRow label="Source"          value={sourceLabel} testid={`queue-detail-${row.id}-source`}/>
            <DetailRow icon={Users} label="Number of riders" value={row.passengers ?? NOT_RECORDED} testid={`queue-detail-${row.id}-riders`}/>
            <DetailRow icon={User}  label="Assigned driver" value={row.assigned_driver_name || "Unassigned"} testid={`queue-detail-${row.id}-driver`} valueClass={row.assigned_driver_name ? "" : "text-zinc-500"}/>

            {/* Editable price (locked once completed) */}
            {!isCompleted && (
            <div className="flex items-start gap-2">
              <DollarSign className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Trip price</div>
                {editPrice ? (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="relative flex-1 min-w-0">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                      <input type="number" min="0" step="0.01" value={priceVal} onChange={(e) => setPriceVal(e.target.value)}
                        className="w-full h-8 pl-5 pr-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100" data-testid={`queue-detail-${row.id}-price-input`}/>
                    </div>
                    <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold" disabled={busy} data-testid={`queue-detail-${row.id}-price-save`} onClick={savePrice}>
                      {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-zinc-400" onClick={() => { setEditPrice(false); setPriceVal(row.total != null ? String(row.total) : ""); }}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-base font-black text-amber-400 tabular-nums" data-testid={`queue-detail-${row.id}-price`}>{fmtUsd(row.total)}</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditPrice(true)} data-testid={`queue-detail-${row.id}-price-edit`}>
                      <Pencil className="h-3 w-3 mr-1"/>Edit price
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-sky-500/40 text-sky-300 hover:text-sky-200" disabled={quoting} onClick={doQuote} data-testid={`queue-detail-${row.id}-quote`}>
                      {quoting ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <Calculator className="h-3 w-3 mr-1"/>}Quote
                    </Button>
                  </div>
                )}
                {quoteHint && <div className="text-[10px] text-sky-300/90 mt-1">{quoteHint} — review &amp; Save</div>}
              </div>
            </div>
            )}
            {isCompleted && (
              <DetailRow icon={DollarSign} label="Trip price" value={fmtUsd(row.total)} testid={`queue-detail-${row.id}-price`} valueClass="text-base font-black text-amber-400 tabular-nums"/>
            )}

            {/* PROMPT 3 · Dual notes — private internal + driver-visible, auto-save. */}
            <DualNotes row={row} onAutoSave={onAutoSave} idPrefix={`queue-detail-${row.id}`}/>
          </section>

          {/* Actions */}
          <section className="space-y-2 border-t border-zinc-800 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Actions</div>
            {isCompleted ? (
              <div className="text-[11px] text-zinc-500" data-testid={`queue-detail-${row.id}-completed-note`}>This trip is completed. Notes can still be edited; fare, driver and flags are locked.</div>
            ) : (
            <>
            <div className="flex gap-1">
              <select value={pick} onChange={(e) => setPick(e.target.value)}
                className="flex-1 h-8 px-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100" data-testid={`queue-detail-${row.id}-reassign-select`}>
                <option value="">{row.assigned_driver_id ? "Reassign to…" : "Assign a driver…"}</option>
                {drivers.map((d) => (<option key={d.id} value={d.id}>{d.online ? "🟢" : "⚪"} {d.name}</option>))}
              </select>
              <Button size="sm" onClick={submitReassign} disabled={!pick || busy} data-testid={`queue-detail-${row.id}-reassign-submit`} className="h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold">
                {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : (row.assigned_driver_id ? "Reassign" : "Assign")}
              </Button>
            </div>
            <input
              type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for handoff (optional) — shown to the new driver & logged to the ride audit" maxLength={300}
              data-testid={`queue-detail-${row.id}-reassign-reason`}
              className="w-full h-8 px-2 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100 placeholder:text-zinc-600"
            />
            <Button
              size="sm" variant="outline" onClick={() => onAssignNearest(row.id)} disabled={busy}
              data-testid={`queue-detail-${row.id}-nearest`}
              className="w-full h-8 text-xs border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><Navigation className="h-3.5 w-3.5 mr-1.5"/>Hand to nearest online driver</>}
            </Button>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => onPatch(row.id, { urgent: !row.urgent })} disabled={busy} data-testid={`queue-detail-${row.id}-urgent`}
                className={`h-8 text-xs ${row.urgent ? "border-red-500/70 text-red-300 bg-red-500/10 hover:bg-red-500/20" : "border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/60"}`}>
                <Flag className={`h-3.5 w-3.5 mr-1 ${row.urgent ? "fill-red-400/50" : ""}`}/>{row.urgent ? "Urgent" : "Flag urgent"}
              </Button>
              {isOnline && (
                <Button size="sm" onClick={() => onComplete(row.id)} disabled={busy} data-testid={`queue-detail-${row.id}-complete`} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Check className="h-3.5 w-3.5 mr-1"/>Complete</>}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onCancel(row.id)} data-testid={`queue-detail-${row.id}-cancel`} className="h-8 text-rose-400 hover:text-rose-300 ml-auto">
                <X className="h-3.5 w-3.5 mr-1"/>Cancel ride
              </Button>
            </div>
            </>
            )}
            <Button size="sm" variant="outline" onClick={requestCloseDetail} data-testid={`queue-detail-${row.id}-close`} className="w-full h-8 text-xs">
              Close
            </Button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}


export default function LiveQueueColumn() {
  const [rows,    setRows]    = useState([]);
  const [completedRows, setCompletedRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId,  setBusyId]  = useState(null);
  const [search,  setSearch]  = useState("");
  const [showPurge, setShowPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  // v2.8+E290 · Remember the dispatcher's last-selected view between visits.
  const [view,    setView]    = useState(() => {
    try { return localStorage.getItem("rtg_coord_board_view") || "all"; } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("rtg_coord_board_view", view); } catch { /* quota */ }
  }, [view]);
  // v2.8+E291 · Local ticker so wait timers + auto-escalation update live
  // between the 20s data polls (minute granularity → 30s is plenty).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const [q, d, c] = await Promise.all([
        adminApi.get("/admin/queued-trips?state=any"),
        adminApi.get("/admin/queued-trips/active-drivers"),
        adminApi.get("/admin/queued-trips?state=completed&limit=50"),
      ]);
      setRows((q.data.queued || []).filter((r) => !["completed", "cancelled", "canceled"].includes(r.state)).sort((a, b) => (a.created_at < b.created_at ? 1 : -1)));
      setDrivers(d.data.drivers || []);
      // PROMPT 3 · Completed trips (recent 50) — note-editable, actions locked.
      setCompletedRows((c.data.queued || []).sort((a, b) => (a.created_at < b.created_at ? 1 : -1)));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const onEvt = () => load();
    // v2.7.65+E146+E147 · Any queue lifecycle frame refreshes the column.
    ["rtg:new_trip_created", "rtg:queued_trip_started", "rtg:queued_trip_completed", "rtg:queued_trip_cancelled"].forEach(
      (k) => window.addEventListener(k, onEvt),
    );
    // Poll every 20 s as a fallback if the WS drops.
    const t = setInterval(load, 20_000);
    return () => {
      clearInterval(t);
      ["rtg:new_trip_created", "rtg:queued_trip_started", "rtg:queued_trip_completed", "rtg:queued_trip_cancelled"].forEach(
        (k) => window.removeEventListener(k, onEvt),
      );
    };
  }, [load]);

  // v2.8+E295 · Distinct URGENT alarm trigger. When an unassigned ride becomes
  // effectively urgent (manual flag OR >5-min auto-escalation) fire the priority
  // klaxon ONCE per ride id; silence it (per id) the moment the ride is handled
  // (assigned / de-escalated / off the board). Re-evaluated on every 20s poll
  // AND the 30s local tick so time-based escalations alert without a refresh.
  const firedUrgentIds = useRef(new Set());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentlyUrgent = new Set(
      rows
        .filter((r) => !r.assigned_driver_id && r.state === "queued" && isEffUrgent(r))
        .map((r) => r.id),
    );
    currentlyUrgent.forEach((id) => {
      if (!firedUrgentIds.current.has(id)) {
        firedUrgentIds.current.add(id);
        try { window.dispatchEvent(new CustomEvent("rtg:booking_urgent", { detail: { id } })); } catch { /* noop */ }
      }
    });
    firedUrgentIds.current.forEach((id) => {
      if (!currentlyUrgent.has(id)) {
        firedUrgentIds.current.delete(id);
        try { window.dispatchEvent(new CustomEvent("rtg:booking_urgent_acknowledged", { detail: { id } })); } catch { /* noop */ }
      }
    });
  }, [rows, tick]);

  // v2.8+E299-fix · Silence THIS board's urgent alarms when the coordinator
  // unmounts (the shared audio hook lives on the dashboard root and would
  // otherwise keep looping — and re-arm on audio-leader handoff — with no
  // board visible to acknowledge them). Unmount-only so it never flickers the
  // live alarm on every poll/tick re-run.
  useEffect(() => {
    return () => {
      firedUrgentIds.current.forEach((id) => {
        try { window.dispatchEvent(new CustomEvent("rtg:booking_urgent_acknowledged", { detail: { id } })); } catch { /* noop */ }
      });
      firedUrgentIds.current.clear();
    };
  }, []);

  // v2.8+E322 · Critical rider-flag chime. When a ride carrying a CRITICAL flag
  // (Banned Rider / Possible Violent) newly lands in the Live Queue, play a
  // SHORT one-shot alert tone. Rides already on the board at mount are seeded
  // silently so the chime fires only for NEW arrivals (not on every refresh).
  const firedCriticalIds = useRef(new Set());
  const criticalSeeded = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentCritical = new Set(
      rows.filter((r) => hasCriticalFlag(r.rider_flags)).map((r) => r.id),
    );
    if (!criticalSeeded.current) {
      criticalSeeded.current = true;
      firedCriticalIds.current = currentCritical;
      return;
    }
    currentCritical.forEach((id) => {
      if (!firedCriticalIds.current.has(id)) {
        firedCriticalIds.current.add(id);
        try { window.dispatchEvent(new CustomEvent("rtg:flag_critical", { detail: { id } })); } catch { /* noop */ }
      }
    });
    firedCriticalIds.current.forEach((id) => {
      if (!currentCritical.has(id)) firedCriticalIds.current.delete(id);
    });
  }, [rows]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this queued ride?")) return;
    setBusyId(id);
    try {
      await adminApi.delete(`/admin/queued-trips/${id}`);
      toast.success("Ride cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusyId(null); }
  };

  const reassign = async (id, driverId, driverName, wasAssigned = false, reason) => {
    setBusyId(id);
    try {
      await adminApi.post(`/admin/queued-trips/${id}/reassign`, {
        assigned_driver_id:   driverId,
        assigned_driver_name: driverName,
        reason:               reason || null,
      });
      const who = driverName || driverId;
      // v2.8+E288 · Clear confirmation the ride actually went out. On a FIRST
      // assignment (rider-app bookings start unassigned) show "Pushed to …";
      // on a driver change show "Reassigned to …".
      toast.success(
        wasAssigned ? `Reassigned to ${who}` : `🚕 Pushed to ${who} — now in their driver app`,
        { duration: 5000 },
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    } finally { setBusyId(null); }
  };

  // v2.8+E299 · One-tap handoff to the nearest online driver (no scrolling).
  const assignNearest = async (id) => {
    setBusyId(id);
    try {
      const { data } = await adminApi.post(`/admin/queued-trips/${id}/assign-nearest`);
      const who = data?.assigned_driver_name || "driver";
      const miles = data?.miles != null ? ` · ${data.miles} mi away` : "";
      toast.success(`🚕 Handed to nearest: ${who}${miles}`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No nearby online driver to hand off to");
    } finally { setBusyId(null); }
  };

  const complete = async (id) => {
    if (!window.confirm("Mark this ride complete? This closes it out and records the fare.")) return;
    setBusyId(id);
    try {
      await adminApi.post(`/admin/queued-trips/${id}/complete`);
      toast.success("Ride completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally { setBusyId(null); }
  };

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      await adminApi.patch(`/admin/queued-trips/${id}`, body);
      toast.success("Saved");
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally { setBusyId(null); }
  };

  const quote = async (id) => {
    try {
      const { data } = await adminApi.post(`/admin/queued-trips/${id}/quote`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quote failed");
      return null;
    }
  };

  // PROMPT 3 · Auto-save notes (internal + driver) WITHOUT a full board reload
  // so the textarea keeps focus and other cards don't flicker. Patches the
  // edited row in place across both the active board and the completed list.
  const autoSaveNotes = useCallback(async (id, body) => {
    try {
      await adminApi.patch(`/admin/queued-trips/${id}`, body);
      const patchRow = (r) =>
        r.id === id
          ? { ...r, ...body, ...(body.notes != null ? { dispatch_notes: body.notes } : {}) }
          : r;
      setRows((prev) => prev.map(patchRow));
      setCompletedRows((prev) => prev.map(patchRow));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Note auto-save failed");
      return false;
    }
  }, []);

  // v2.8+E291 · Bulk stale-purge — soft-cancels UNASSIGNED rides older than the
  // chosen window (assigned/in-progress rides are never touched).
  const purgeStale = async (hours) => {
    const label = hours === 48 ? "48 hours" : "24 hours";
    if (!window.confirm(`Cancel every UNASSIGNED ride older than ${label}?\n\nAssigned and in-progress rides are never touched. Records stay in the database for reporting.`)) return;
    setPurging(true);
    try {
      const { data } = await adminApi.post("/admin/queued-trips/purge-stale", { older_than_hours: hours });
      toast.success(`Purged ${data.cancelled} stale ride${data.cancelled === 1 ? "" : "s"} off the board`);
      setShowPurge(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setPurging(false); }
  };

  const grouped = useMemo(() => {
    // v2.8+E289/E290 · Urgent-flagged AND auto-escalated rides sort to the top
    // of each section (rows arrive newest-first → stable urgent-first).
    const byUrgent = (a, b) => (isEffUrgent(b) ? 1 : 0) - (isEffUrgent(a) ? 1 : 0);
    // v2.8+E290 · Board search — match rider name or phone.
    const q = search.trim().toLowerCase();
    const src = q
      ? rows.filter((r) =>
          (r.rider_name || "").toLowerCase().includes(q) ||
          (r.rider_phone || "").toLowerCase().includes(q))
      : rows;
    return {
      unassigned: src.filter((r) => !r.assigned_driver_id && r.state === "queued").sort(byUrgent),
      queued:     src.filter((r) => r.assigned_driver_id && r.state === "queued").sort(byUrgent),
      active:     src.filter((r) => r.state === "in_progress").sort(byUrgent),
      completed:  (q
        ? completedRows.filter((r) =>
            (r.rider_name || "").toLowerCase().includes(q) ||
            (r.rider_phone || "").toLowerCase().includes(q))
        : completedRows),
    };
  }, [rows, completedRows, search]);

  return (
    <div className="space-y-3" data-testid="live-queue-column">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-amber-400 animate-pulse"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">Live Queue</div>
            <div className="text-sm font-bold text-zinc-100">{rows.length} ride{rows.length === 1 ? "" : "s"} on the board</div>
          </div>
        </div>
        {rows.length > 0 && (
          <div className="relative">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowPurge((v) => !v)}
              data-testid="queue-purge-btn"
              className="h-7 text-[10px] text-zinc-400 border-zinc-700 hover:text-rose-300 hover:border-rose-500/60"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1"/>Purge stale
            </Button>
            {showPurge && (
              <div className="absolute right-0 mt-1 z-20 w-56 rounded-lg border border-zinc-700 bg-zinc-950 p-2 shadow-xl space-y-1" data-testid="queue-purge-menu">
                <div className="text-[10px] text-zinc-500 px-1 pb-1 leading-snug">Cancel UNASSIGNED rides older than <span className="text-zinc-400">(assigned rides are safe)</span>:</div>
                <button onClick={() => purgeStale(24)} disabled={purging} data-testid="queue-purge-24" className="w-full text-left text-xs text-zinc-200 hover:bg-rose-500/10 hover:text-rose-200 rounded px-2 py-1.5 disabled:opacity-50">24 hours</button>
                <button onClick={() => purgeStale(48)} disabled={purging} data-testid="queue-purge-48" className="w-full text-left text-xs text-zinc-200 hover:bg-rose-500/10 hover:text-rose-200 rounded px-2 py-1.5 disabled:opacity-50">48 hours</button>
              </div>
            )}
          </div>
        )}
      </div>
      {loading ? (
        <div className="text-zinc-500 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading…</div>
      ) : rows.length === 0 && completedRows.length === 0 ? (
        <Card className="p-6 text-center text-sm text-zinc-500" data-testid="live-queue-empty">
          Board is clear. New dispatches land here in real time.
        </Card>
      ) : (
        <>
          {/* v2.8+E290 · Quick search — find a rider by name or phone. */}
          <div className="relative" data-testid="queue-search">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500 pointer-events-none"/>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rider name or phone…"
              data-testid="queue-search-input"
              className="w-full h-8 pl-8 pr-8 rounded-md border border-zinc-700 bg-zinc-950 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400/60 outline-none"
            />
            {search && (
              <button onClick={() => setSearch("")} data-testid="queue-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                <X className="h-3.5 w-3.5"/>
              </button>
            )}
          </div>
          {/* v2.8+E289 · One-tap views so dispatch can isolate rides already
              pushed to a driver ("Assigned") vs still waiting ("Unassigned"). */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="queue-filter">
            {[
              { k: "all",        label: "All",         n: grouped.unassigned.length + grouped.queued.length + grouped.active.length },
              { k: "unassigned", label: "Unassigned",  n: grouped.unassigned.length },
              { k: "assigned",   label: "Assigned",    n: grouped.queued.length },
              { k: "inprogress", label: "In Progress", n: grouped.active.length },
              { k: "completed",  label: "Completed",   n: grouped.completed.length },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setView(f.k)}
                data-testid={`queue-filter-${f.k}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                  view === f.k ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {f.label} <span className="font-mono">{f.n}</span>
              </button>
            ))}
          </div>
          {/* v2.8+E317 · Status color legend — decode the card status accents
              at a glance so dispatch reads the board without hunting. */}
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[9px] text-zinc-500" data-testid="queue-legend">
            {[
              { c: "bg-amber-400",   l: "Unassigned" },
              { c: "bg-sky-400",     l: "Assigned" },
              { c: "bg-emerald-400", l: "In Progress" },
              { c: "bg-red-500",     l: "Urgent" },
            ].map((x) => (
              <span key={x.l} className="inline-flex items-center gap-1" data-testid={`queue-legend-${x.l.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className={`h-2 w-2 rounded-full ${x.c}`}/>{x.l}
              </span>
            ))}
          </div>
          {(view === "all" || view === "unassigned") && grouped.unassigned.length > 0 && (
            <section className="space-y-2">
              <div className="text-[9px] uppercase tracking-widest text-amber-400 font-bold">Unassigned · {grouped.unassigned.length}</div>
              {grouped.unassigned.map((r) => <QueueCard key={r.id} row={r} drivers={drivers} onCancel={cancel} onReassign={reassign} onComplete={complete} onPatch={patch} onQuote={quote} onAssignNearest={assignNearest} onAutoSave={autoSaveNotes} busyId={busyId}/>)}
            </section>
          )}
          {(view === "all" || view === "assigned") && grouped.queued.length > 0 && (
            <section className="space-y-2">
              <div className="text-[9px] uppercase tracking-widest text-sky-400 font-bold">Assigned · {grouped.queued.length}</div>
              {grouped.queued.map((r) => <QueueCard key={r.id} row={r} drivers={drivers} onCancel={cancel} onReassign={reassign} onComplete={complete} onPatch={patch} onQuote={quote} onAssignNearest={assignNearest} onAutoSave={autoSaveNotes} busyId={busyId}/>)}
            </section>
          )}
          {(view === "all" || view === "inprogress") && grouped.active.length > 0 && (
            <section className="space-y-2">
              <div className="text-[9px] uppercase tracking-widest text-emerald-400 font-bold">In Progress · {grouped.active.length}</div>
              {grouped.active.map((r) => <QueueCard key={r.id} row={r} drivers={drivers} onCancel={cancel} onReassign={reassign} onComplete={complete} onPatch={patch} onQuote={quote} onAssignNearest={assignNearest} onAutoSave={autoSaveNotes} busyId={busyId}/>)}
            </section>
          )}
          {view === "completed" && grouped.completed.length > 0 && (
            <section className="space-y-2" data-testid="queue-completed-section">
              <div className="text-[9px] uppercase tracking-widest text-emerald-300/80 font-bold">Completed · {grouped.completed.length} <span className="text-zinc-600 normal-case tracking-normal">(recent · notes editable)</span></div>
              {grouped.completed.map((r) => <QueueCard key={r.id} row={r} drivers={drivers} onCancel={cancel} onReassign={reassign} onComplete={complete} onPatch={patch} onQuote={quote} onAssignNearest={assignNearest} onAutoSave={autoSaveNotes} busyId={busyId}/>)}
            </section>
          )}
          {(
            (view === "all"        && grouped.unassigned.length + grouped.queued.length + grouped.active.length === 0) ||
            (view === "unassigned" && grouped.unassigned.length === 0) ||
            (view === "assigned"   && grouped.queued.length === 0) ||
            (view === "inprogress" && grouped.active.length === 0) ||
            (view === "completed"  && grouped.completed.length === 0)
          ) && (
            <Card className="p-6 text-center text-sm text-zinc-500" data-testid="queue-filter-empty">
              {search.trim() ? `No rides match “${search.trim()}”.` : "No rides in this view."}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
